<template>
  <div class="mobile-learning-analysis-page">
    <van-nav-bar title="学情分析" fixed placeholder />

    <van-pull-refresh v-model="refreshing" @refresh="handleRefresh">
      <div class="page-content">
        <!-- 顶部搜索筛选区 -->
        <div class="search-card">
          <van-field
              v-model="searchForm.username"
              label="账号"
              placeholder="请输入账号"
              clearable
          />

          <van-field
              v-model="searchForm.realName"
              label="姓名"
              placeholder="请输入姓名"
              clearable
          />

          <div class="filter-row">
            <div class="filter-item" @click="showSubjectPicker = true">
              <span class="filter-label">科目</span>
              <span class="filter-value">
                {{ searchForm.subjectName || '全部科目' }}
              </span>
            </div>

            <div class="filter-item" @click="showSuggestionPicker = true">
              <span class="filter-label">建议</span>
              <span class="filter-value">
                {{ suggestionText || '全部建议' }}
              </span>
            </div>
          </div>

          <div class="search-actions">
            <van-button
                round
                plain
                block
                type="default"
                size="small"
                @click="resetSearch"
            >
              重置
            </van-button>

            <van-button
                round
                block
                type="info"
                size="small"
                @click="submitSearch"
            >
              查询
            </van-button>
          </div>
        </div>

        <!-- 顶部统计 -->
        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-title">学员总数</div>
            <div class="summary-value">{{ summary.totalStudentCount || 0 }}</div>
          </div>

          <div class="summary-card">
            <div class="summary-title">完成全题练习</div>
            <div class="summary-value">{{ summary.finishedAllQuestionCount || 0 }}</div>
          </div>

          <div class="summary-card recommend">
            <div class="summary-title">建议考试</div>
            <div class="summary-value">{{ summary.recommendExamCount || 0 }}</div>
          </div>

          <div class="summary-card observe">
            <div class="summary-title">继续观察</div>
            <div class="summary-value">{{ summary.observeCount || 0 }}</div>
          </div>
        </div>

        <div class="list-title-row">
          <span>建议参考学员</span>
          <span class="total-text">共 {{ pagination.total }} 人</span>
        </div>

        <template v-if="studentList.length > 0">
          <div
              v-for="item in studentList"
              :key="item.userId"
              class="analysis-card"
          >
            <div class="card-header">
              <div class="student-info">
                <div class="student-name">
                  {{ item.realName || '-' }}
                </div>
                <div class="student-account">
                  账号：{{ item.username || '-' }}
                </div>
              </div>

              <van-tag :type="getSuggestionTagType(item.suggestionCode)">
                {{ item.suggestionText || formatSuggestionText(item.suggestionCode) }}
              </van-tag>
            </div>

            <div class="info-line">
              <span>所属科目：</span>{{ item.subjectName || '-' }}
            </div>

            <div class="metric-row">
              <div class="metric-item">
                <div class="metric-label">学习覆盖</div>
                <div class="metric-value">
                  {{ item.learnedQuestionCount || 0 }}
                  <span>/ {{ item.totalQuestionCount || 0 }}</span>
                </div>
              </div>

              <div class="metric-item">
                <div class="metric-label">覆盖率</div>
                <div class="metric-value blue">
                  {{ item.coverageRate == null ? 0 : item.coverageRate }}%
                </div>
              </div>

              <div class="metric-item">
                <div class="metric-label">当前错题</div>
                <div class="metric-value red">
                  {{ item.currentWrongCount || 0 }}
                </div>
              </div>
            </div>

            <div class="exam-row">
              <div>
                <span>最近模考：</span>
                <strong>{{ item.latestExamScore == null ? '-' : item.latestExamScore }}</strong>
              </div>
              <div>
                <span>最近三次通过：</span>
                <strong>{{ item.recentThreeExamPassCount || 0 }}</strong>
              </div>
            </div>

            <div class="reason-box">
              <div class="reason-title">判定原因</div>
              <div class="reason-text">
                {{ item.reasonText || '暂无说明' }}
              </div>
            </div>

            <div class="card-actions">
              <van-button
                  size="small"
                  type="info"
                  plain
                  round
                  @click="goLearningDetail(item)"
              >
                查看学习情况
              </van-button>
            </div>
          </div>

          <!-- 分页 -->
          <div v-if="pagination.total > pagination.pageSize" class="pagination-box">
            <van-pagination
                v-model="pagination.pageNum"
                :total-items="pagination.total"
                :items-per-page="pagination.pageSize"
                mode="simple"
                @change="handlePageChange"
            />
          </div>
        </template>

        <van-empty
            v-else
            description="暂无学情分析数据"
        />
      </div>
    </van-pull-refresh>

    <!-- 科目选择 -->
    <van-popup v-model="showSubjectPicker" position="bottom">
      <van-picker
          show-toolbar
          title="选择科目"
          :columns="subjectPickerColumns"
          @confirm="onSubjectConfirm"
          @cancel="showSubjectPicker = false"
      />
    </van-popup>

    <!-- 建议结果选择 -->
    <van-popup v-model="showSuggestionPicker" position="bottom">
      <van-picker
          show-toolbar
          title="选择建议结果"
          :columns="suggestionPickerColumns"
          @confirm="onSuggestionConfirm"
          @cancel="showSuggestionPicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import { Toast } from 'vant'
import {
  getLearningAnalysisSummary,
  getLearningAnalysisRecommendList
} from '@/api/admin/learningAnalysis'
import { getSubjectMappingOptions } from '@/api/admin/subjectMapping'

export default {
  name: 'MobileAdminLearningAnalysis',

  data() {
    return {
      loading: false,
      refreshing: false,

      summary: {
        totalStudentCount: 0,
        finishedAllQuestionCount: 0,
        recommendExamCount: 0,
        observeCount: 0
      },

      studentList: [],
      subjectOptions: [],

      pagination: {
        pageNum: 1,
        pageSize: 4,
        total: 0
      },

      searchForm: {
        username: '',
        realName: '',
        subjectName: '',
        suggestionCode: ''
      },

      showSubjectPicker: false,
      showSuggestionPicker: false,

      suggestionPickerColumns: [
        { text: '全部建议', value: '' },
        { text: '建议考试', value: 'RECOMMEND' },
        { text: '可继续观察', value: 'OBSERVE' },
        { text: '暂不建议', value: 'NOT_RECOMMEND' }
      ]
    }
  },

  computed: {
    subjectPickerColumns() {
      return [
        { text: '全部科目', value: '' },
        ...this.subjectOptions.map(item => ({
          text: item.subjectName,
          value: item.subjectName
        }))
      ]
    },

    suggestionText() {
      const item = this.suggestionPickerColumns.find(i => i.value === this.searchForm.suggestionCode)
      return item ? item.text : ''
    }
  },

  created() {
    this.loadSubjectOptions()
    this.refreshAll()
  },

  methods: {
    async handleRefresh() {
      await this.refreshAll()
      this.refreshing = false
    },

    async refreshAll() {
      await Promise.all([
        this.loadSummary(),
        this.loadRecommendList(true)
      ])
    },

    async loadSubjectOptions() {
      try {
        const res = await getSubjectMappingOptions()

        if (res.code === 200) {
          this.subjectOptions = res.data || []
        } else {
          Toast.fail(res.message || '获取科目列表失败')
        }
      } catch (e) {
        Toast.fail('获取科目列表失败')
      }
    },

    async loadSummary() {
      try {
        const res = await getLearningAnalysisSummary()

        if (res.code === 200) {
          this.summary = res.data || {
            totalStudentCount: 0,
            finishedAllQuestionCount: 0,
            recommendExamCount: 0,
            observeCount: 0
          }
        } else {
          Toast.fail(res.message || '获取学情概览失败')
        }
      } catch (e) {
        Toast.fail('获取学情概览失败')
      }
    },

    async loadRecommendList(reset) {
      if (this.loading) {
        return
      }

      if (reset) {
        this.pagination.pageNum = 1
      }

      this.loading = true

      try {
        const params = {
          username: this.searchForm.username,
          realName: this.searchForm.realName,
          subjectName: this.searchForm.subjectName,
          suggestionCode: this.searchForm.suggestionCode,
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }

        const res = await getLearningAnalysisRecommendList(params)

        if (res.code === 200) {
          const data = res.data || {}

          this.studentList = data.records || []
          this.pagination.total = data.total || 0
        } else {
          Toast.fail(res.message || '获取学情列表失败')
        }
      } catch (e) {
        Toast.fail('获取学情列表失败')
      } finally {
        this.loading = false
      }
    },

    handlePageChange(page) {
      this.pagination.pageNum = page
      this.loadRecommendList(false)
    },

    submitSearch() {
      this.loadRecommendList(true)
    },

    resetSearch() {
      this.searchForm = {
        username: '',
        realName: '',
        subjectName: '',
        suggestionCode: ''
      }

      this.loadRecommendList(true)
    },

    onSubjectConfirm(item) {
      this.searchForm.subjectName = item.value
      this.showSubjectPicker = false
    },

    onSuggestionConfirm(item) {
      this.searchForm.suggestionCode = item.value
      this.showSuggestionPicker = false
    },

    getSuggestionTagType(code) {
      if (code === 'RECOMMEND') {
        return 'success'
      }

      if (code === 'OBSERVE') {
        return 'warning'
      }

      if (code === 'NOT_RECOMMEND') {
        return 'danger'
      }

      return 'default'
    },

    formatSuggestionText(code) {
      if (code === 'RECOMMEND') {
        return '建议考试'
      }

      if (code === 'OBSERVE') {
        return '可继续观察'
      }

      if (code === 'NOT_RECOMMEND') {
        return '暂不建议'
      }

      return '-'
    },

    goLearningDetail(item) {
      this.$router.push({
        path: '/m/admin/student/learning-detail',
        query: {
          id: item.userId,
          realName: item.realName || '',
          username: item.username || ''
        }
      })
    }
  }
}
</script>

<style scoped>
.mobile-learning-analysis-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.page-content {
  padding: 12px;
  box-sizing: border-box;
}

.search-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid #f2f3f5;
  border-bottom: 1px solid #f2f3f5;
}

.filter-item {
  padding: 12px;
  box-sizing: border-box;
  font-size: 14px;
}

.filter-item:first-child {
  border-right: 1px solid #f2f3f5;
}

.filter-label {
  color: #646566;
  margin-right: 6px;
}

.filter-value {
  color: #1989fa;
}

.search-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.summary-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.summary-title {
  color: #606266;
  font-size: 13px;
  margin-bottom: 8px;
}

.summary-value {
  color: #1989fa;
  font-size: 28px;
  line-height: 32px;
  font-weight: bold;
}

.summary-card.recommend .summary-value {
  color: #07c160;
}

.summary-card.observe .summary-value {
  color: #ff976a;
}

.list-title-row {
  margin: 16px 2px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #303133;
  font-size: 16px;
  font-weight: bold;
}

.total-text {
  color: #909399;
  font-size: 13px;
  font-weight: normal;
}

.analysis-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.student-info {
  flex: 1;
  min-width: 0;
}

.student-name {
  color: #303133;
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 5px;
}

.student-account {
  color: #909399;
  font-size: 13px;
}

.info-line {
  color: #606266;
  font-size: 13px;
  line-height: 24px;
}

.info-line span {
  color: #909399;
}

.metric-row {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.metric-item {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 10px 4px;
  text-align: center;
}

.metric-label {
  color: #909399;
  font-size: 12px;
  margin-bottom: 6px;
}

.metric-value {
  color: #303133;
  font-size: 17px;
  font-weight: bold;
}

.metric-value span {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.metric-value.blue {
  color: #1989fa;
}

.metric-value.red {
  color: #ee0a24;
}

.exam-row {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  color: #606266;
  font-size: 13px;
}

.exam-row span {
  color: #909399;
}

.exam-row strong {
  color: #303133;
}

.reason-box {
  margin-top: 12px;
  background: #f7f8fa;
  border-radius: 10px;
  padding: 10px;
}

.reason-title {
  color: #303133;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 6px;
}

.reason-text {
  color: #606266;
  font-size: 13px;
  line-height: 21px;
  white-space: pre-wrap;
  word-break: break-word;
}

.card-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.pagination-box {
  margin: 16px 0 4px;
  background: #fff;
  border-radius: 12px;
  padding: 8px 0;
  overflow: hidden;
}
</style>